import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kExternalStorageBasePath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { ImportFolderResponse, ImportFolderResponseSchema } from '@/user_files/external-storage/schemas'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'

export async function postImportFolder(params: {
  accountId: string
  externalFolderId: string
  matterId: string
  parentUserFolderId?: string
  replaceExistingMapping?: boolean
}): Promise<ImportFolderResponse> {
  const sentryTransactionId = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kExternalStorageBasePath}/accounts/${encodeURIComponent(params.accountId)}/import-folder`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
      },
      body: JSON.stringify({
        external_folder_id: params.externalFolderId,
        matter_id: params.matterId,
        ...(params.parentUserFolderId ? { parent_user_folder_id: params.parentUserFolderId } : {}),
        ...(params.replaceExistingMapping ? { replace_existing_mapping: true } : {}),
      }),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatUserFilesErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            ...params,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, detail || `Failed to import folder: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Import folder response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            ...params,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid import folder response JSON')
    }

    const parsed = ImportFolderResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Import folder response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            ...params,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid import folder response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, { extra: { ...params } })
      })
    }
    throw error
  }
}
